const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let _accessToken: string | null = null;
let _onUnauthorized: (() => void) | null = null;
let _refreshingPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function onUnauthorized(cb: () => void): void {
  _onUnauthorized = cb;
}

async function tryRefresh(): Promise<string | null> {
  if (_refreshingPromise) return _refreshingPromise;
  _refreshingPromise = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { access_token: string };
      _accessToken = body.access_token;
      return _accessToken;
    } catch {
      return null;
    } finally {
      _refreshingPromise = null;
    }
  })();
  return _refreshingPromise;
}

interface ApiOptions {
  baseUrl?: string;
  skipRefresh?: boolean;
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  { baseUrl = API_URL, skipRefresh = false }: ApiOptions = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', headers.get('Content-Type') ?? 'application/json');
  if (_accessToken) headers.set('Authorization', `Bearer ${_accessToken}`);

  const doFetch = async () =>
    fetch(`${baseUrl}${path}`, { credentials: 'include', ...init, headers });

  let res = await doFetch();

  if (res.status === 401 && !skipRefresh && path !== '/api/v1/auth/refresh') {
    const newToken = await tryRefresh();
    if (newToken) {
      headers.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(`${baseUrl}${path}`, { credentials: 'include', ...init, headers });
    } else if (_onUnauthorized) {
      _onUnauthorized();
    }
  }

  if (!res.ok) {
    let code: string | undefined;
    let message = res.statusText;
    let details: unknown;
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string; details?: unknown };
      };
      code = body.error?.code;
      message = body.error?.message ?? message;
      details = body.error?.details;
    } catch {
      /* noop */
    }
    throw new ApiError(message, res.status, code, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------- tipos compartilhados ----------------

export interface HealthResponse {
  status: string;
  version: string;
  env: string;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  timezone: string;
  locale: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface Household {
  id: string;
  name: string;
  created_at: string;
}

export type Role = 'owner' | 'member';

export interface MeResponse {
  user: User;
  household: Household | null;
  role: Role | null;
}

export interface InviteInfo {
  household_name: string;
  inviter_name: string;
  role: Role;
  email: string | null;
  expires_at: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
}

// ---------------- calls ----------------

export const fetchHealth = () => api<HealthResponse>('/healthz');

export const fetchMe = () => api<MeResponse>('/api/v1/auth/me');

export const login = (email: string, password: string) =>
  api<TokenResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const logout = () =>
  api<void>('/api/v1/auth/logout', { method: 'POST' });

export const register = (args: {
  email: string;
  full_name: string;
  password: string;
  invite_token: string;
}) =>
  api<User>('/api/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify(args),
  });

export const fetchInvite = (token: string) =>
  api<InviteInfo>(`/api/v1/invites/${encodeURIComponent(token)}`, { method: 'GET' }, { skipRefresh: true });

export interface HouseholdMember {
  user_id: string;
  household_id: string;
  role: Role;
  joined_at: string;
  user: User;
}

export interface PasswordResetCreated {
  token: string;
  reset_url: string;
  expires_at: string;
}

export interface PasswordResetInfo {
  email_masked: string;
  expires_at: string;
}

export const listMembers = (householdId: string) =>
  api<HouseholdMember[]>(`/api/v1/households/${householdId}/members`);

export const createPasswordReset = (userId: string) =>
  api<PasswordResetCreated>('/api/v1/auth/password-resets', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });

export const readPasswordReset = (token: string) =>
  api<PasswordResetInfo>(
    `/api/v1/auth/password-resets/${encodeURIComponent(token)}`,
    { method: 'GET' },
    { skipRefresh: true },
  );

export const consumePasswordReset = (token: string, newPassword: string) =>
  api<void>(
    `/api/v1/auth/password-resets/${encodeURIComponent(token)}`,
    { method: 'POST', body: JSON.stringify({ new_password: newPassword }) },
    { skipRefresh: true },
  );
